// ========================================
// TEAM BUDGET MODAL - EDIT/ADD/DELETE BUDGET
// ========================================

import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import Modal from './shared/Modal';
import ConfirmModal from './shared/ConfirmModal';
import Button from './shared/Button';
import { updateTeamBudget } from '../services/api';

/**
 * TeamBudgetModal Component
 *
 * Modal for managing team budget settings.
 * Features:
 * - Set/Update budget limit (all users)
 * - Set budget duration (admin only)
 * - Remove budget limit (admin only)
 *
 * @param {Object} team - Team object with current budget info
 * @param {Function} onClose - Callback to close modal
 * @param {Function} onUpdate - Callback after budget update
 * @param {Array} allUsers - List of all users (to check admin role)
 */
const TeamBudgetModal = ({ team, onClose, onUpdate, allUsers = [] }) => {
    const { accounts } = useMsal();
    const { t } = useTranslation();
    const currentUserEmail = accounts[0]?.username || accounts[0]?.email || 'unknown@aware.co.th';

    // Check if current user is admin (from OpenWebUI role)
    const currentUserIsAdmin = (() => {
        const currentUser = allUsers.find(u =>
            u.email?.toLowerCase() === currentUserEmail?.toLowerCase()
        );
        return currentUser?.role === 'admin';
    })();

    const [budget, setBudget] = useState(team.max_budget || 0);
    const [duration, setDuration] = useState(
        getDurationLabel(team.budget_duration) || 'monthly'
    );
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

    const teamName = team.team_alias || 'Unnamed Team';
    const currentSpend = team.spend || 0;

    /**
     * Convert duration code to label
     */
    function getDurationLabel(durationCode) {
        if (!durationCode) return 'monthly';
        if (durationCode === '1d' || durationCode === '24h') return 'daily';
        if (durationCode === '7d') return 'weekly';
        if (durationCode === '30d') return 'monthly';
        if (durationCode === '365d') return 'yearly';
        return 'monthly';
    }

    /**
     * Handle budget update
     */
    const handleSave = async () => {
        if (budget < 0) {
            setError('Budget cannot be negative');
            return;
        }

        setSaving(true);
        setError(null);

        const result = await updateTeamBudget(
            team.team_id,
            budget,
            duration,
            currentUserEmail,
            team.max_budget,
            teamName,
            true // enforce limit
        );

        if (result.success) {
            if (onUpdate) {
                await onUpdate();
            }
            onClose();
        } else {
            console.error('[BUDGET] Error updating budget:', result.error);
            setError(result.error || 'Failed to update budget');
        }

        setSaving(false);
    };

    /**
     * Show confirmation for removing budget
     */
    const handleRemoveBudget = () => {
        setShowRemoveConfirm(true);
    };

    /**
     * Execute removing budget (set to 0)
     */
    const executeRemoveBudget = async () => {
        setSaving(true);
        setError(null);

        const result = await updateTeamBudget(
            team.team_id,
            0,
            duration,
            currentUserEmail,
            team.max_budget,
            teamName,
            false
        );

        setShowRemoveConfirm(false);

        if (result.success) {
            if (onUpdate) {
                await onUpdate();
            }
            onClose();
        } else {
            setError(result.error || 'Failed to remove budget');
        }

        setSaving(false);
    };

    const renderFooter = () => (
        <>
            <Button onClick={onClose} variant="secondary" disabled={saving}>
                Cancel
            </Button>
            {team.max_budget > 0 && currentUserIsAdmin && (
                <Button
                    onClick={handleRemoveBudget}
                    variant="danger"
                    disabled={saving}
                >
                    Remove Budget
                </Button>
            )}
            <Button onClick={handleSave} variant="primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Budget'}
            </Button>
        </>
    );

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={`Manage Budget: ${teamName}`}
            footer={renderFooter()}
        >
            {error && (
                <div style={{
                    padding: '0.75rem',
                    backgroundColor: 'var(--critical-bg)',
                    border: '1px solid var(--critical)',
                    borderRadius: '6px',
                    marginBottom: '1rem',
                    color: 'var(--critical)'
                }}>
                    {error}
                </div>
            )}

            {/* Current Spend Info */}
            <div style={{
                padding: '1rem',
                backgroundColor: 'var(--surface)',
                borderRadius: '6px',
                marginBottom: '1.5rem'
            }}>
                <div style={{ color: 'var(--muted)', marginBottom: '0.5rem' }}>
                    Current Spend
                </div>
                <div style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: currentSpend > budget && budget > 0 ? 'var(--critical)' : 'var(--success)'
                }}>
                    ${currentSpend.toFixed(2)}
                </div>
            </div>

            {/* Budget Input */}
            <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: 'var(--fg-primary)',
                    fontWeight: '500'
                }}>
                    Budget Limit ($)
                </label>
                <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={budget}
                    onChange={(e) => setBudget(parseFloat(e.target.value) || 0)}
                    style={{
                        width: '100%',
                        padding: '0.75rem',
                        fontSize: '1.1rem',
                        borderRadius: '6px',
                        border: '1px solid var(--border)',
                        backgroundColor: 'var(--bg)',
                        color: 'var(--fg-primary)',
                        boxSizing: 'border-box'
                    }}
                    disabled={saving}
                />
                {currentUserIsAdmin && (
                    <div style={{
                        fontSize: '0.85rem',
                        color: 'var(--muted)',
                        marginTop: '0.5rem'
                    }}>
                        Set to 0 to remove budget limit
                    </div>
                )}
            </div>

            {/* Duration Select - Admin Only */}
            {currentUserIsAdmin && (
                <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        color: 'var(--fg-primary)',
                        fontWeight: '500'
                    }}>
                        Budget Period
                    </label>
                    <select
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '0.75rem',
                            fontSize: '1rem',
                            borderRadius: '6px',
                            border: '1px solid var(--border)',
                            backgroundColor: 'var(--bg)',
                            color: 'var(--fg-primary)',
                            cursor: 'pointer'
                        }}
                        disabled={saving}
                    >
                        <option value="daily">{t('teamManagement.daily')}</option>
                        <option value="weekly">{t('teamManagement.weekly')}</option>
                        <option value="monthly">{t('teamManagement.monthly')}</option>
                        <option value="yearly">{t('teamManagement.yearly')}</option>
                    </select>
                </div>
            )}

            {/* Warning if over budget */}
            {budget > 0 && currentSpend >= budget && (
                <div style={{
                    padding: '0.75rem',
                    backgroundColor: 'var(--warning-bg)',
                    border: '1px solid var(--warning)',
                    borderRadius: '6px',
                    color: 'var(--warning)'
                }}>
                    <strong>Warning:</strong> Current spend (${currentSpend.toFixed(2)})
                    exceeds the new budget limit (${budget.toFixed(2)}).
                    The team may be blocked.
                </div>
            )}

            {/* Remove Budget Confirmation Modal */}
            <ConfirmModal
                isOpen={showRemoveConfirm}
                onClose={() => setShowRemoveConfirm(false)}
                onConfirm={executeRemoveBudget}
                title="Remove Budget Limit"
                message={`Remove budget limit for "${teamName}"?`}
                details="This team will have no spending limit."
                variant="warning"
                confirmLabel="Remove Budget"
                loading={saving}
            />
        </Modal>
    );
};

export default TeamBudgetModal;

